import type {
    Content,
    ExternalShareType,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { Retry } from "../../utils/retryCallable.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface SharePitchContentProps {
    contentId: string;
    version: number;
    externalShareType: ExternalShareType;
}

export function useSharePitchContentAction(
    props: SharePitchContentProps,
): [AsyncState<Content | undefined>, UseAsyncActions<Content | undefined>] {
    const { contentId, version, externalShareType } = props;

    const [externalShareUrlState, externalShareUrlAction] = useAsync(
        async () => {
            const response = await (
                await getDiscoveryApiClient()
            ).getExternalShareUrl(contentId, version, externalShareType);
            return response;
        },
        {},
    );
    useAsyncErrorStatusNotification(externalShareUrlState);

    const [contentDetailsState, contentDetailsStateAction] = useAsync(
        async () => {
            const response = await (
                await getDiscoveryApiClient()
            ).getContentDetails(contentId, externalShareType, true);
            return response;
        },
        {},
    );
    useAsyncErrorStatusNotification(contentDetailsState);

    const [sharePitchContentState, sharePitchContentAction] = useAsync(
        async () => {
            const response = await externalShareUrlAction.execute();
            if (response) {
                const data = await Retry(contentDetailsStateAction.execute, {
                    successCondition: (response) =>
                        response.content?.externalShareUrl !== undefined,
                }).execute();
                return data?.content;
            }
            return {};
        },
    );

    return [sharePitchContentState, sharePitchContentAction];
}
